import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  messageService,
  assistantService,
  sessionService,
  messageQueueService,
  messageTemplateService,
} from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { MessageSchedule, MessageScheduleFilters } from '../types/message';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import MessageList from '../components/messages/MessageList';
import MessageForm from '../components/messages/MessageForm';
import MessageFilters from '../components/messages/MessageFilters';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';

type ModalType = 'create' | 'edit' | '';

function MessageSchedules() {
  const [modal, setModal] = useState<ModalType>('');
  const [editingSchedule, setEditingSchedule] = useState<MessageSchedule | null>(null);
  const [filters, setFilters] = useState<MessageScheduleFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const queryClient = useQueryClient();

  const { data: scheduleResponse, isLoading } = useQuery({
    queryKey: ['message-schedules', filters, currentPage],
    queryFn: () => messageService.getSchedules(filters, currentPage),
  });

  const { data: assistants } = useQuery({
    queryKey: ['assistants'],
    queryFn: () => assistantService.getAssistants(),
  });

  const { data: sessions } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => sessionService.getSessions(),
  });

  const { data: queues } = useQuery({
    queryKey: ['message-queues'],
    queryFn: () => messageQueueService.getQueues(),
  });

  const { data: templates } = useQuery({
    queryKey: ['message-templates'],
    queryFn: () => messageTemplateService.getUserTemplates(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<MessageSchedule>) => messageService.createSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-schedules'] });
      messageHandler.success('Mensagem agendada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: MessageSchedule) => messageService.updateSchedule(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-schedules'] });
      messageHandler.success('Mensagem atualizada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => messageService.deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-schedules'] });
      messageHandler.success('Mensagem excluída com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const clearMutation = useMutation({
    mutationFn: () => messageService.clearSchedules(filters),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['message-schedules'] });
      messageHandler.success(`${data.deleted_count} mensagem(ns) excluída(s) com sucesso!`);
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const messageJsonStr = formData.get('message_json') as string;
    let messageJson = null;

    if (messageJsonStr && messageJsonStr.trim()) {
      try {
        messageJson = JSON.parse(messageJsonStr);
      } catch (error) {
        messageHandler.error('JSON inválido no campo Dados JSON');
        return;
      }
    }

    const data = {
      message: formData.get('message') as string,
      send: formData.get('send') === 'on',
      sendtoPhone: (formData.get('sendtoPhone') as string) || null,
      title: (formData.get('title') as string) || null,
      sendToMail: (formData.get('sendToMail') as string) || null,
      message_json: messageJson,
      assistant_id: formData.get('assistant_id') ? Number(formData.get('assistant_id')) : null,
      whatsapp_id: formData.get('whatsapp_id') ? Number(formData.get('whatsapp_id')) : null,
      template_id: formData.get('template_id') ? Number(formData.get('template_id')) : null,
      priority: formData.get('priority') ? Number(formData.get('priority')) : 99,
      scheduled_at: (formData.get('scheduled_at') as string) || null,
      key: formData.get('key') ? Number(formData.get('key')) : null,
    };

    if (editingSchedule) {
      updateMutation.mutate({ ...editingSchedule, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (schedule: MessageSchedule) => {
    messageHandler.confirm(
      'Excluir Mensagem',
      'Tem certeza que deseja excluir esta mensagem?',
      () => deleteMutation.mutate(schedule.id)
    );
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await messageService.exportSchedulesCsv(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'message_schedules.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      messageHandler.success('Exportação concluída com sucesso!');
    } catch (error) {
      messageHandler.error('Falha ao exportar agendamentos');
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearMessages = () => {
    const messageCount = scheduleResponse?.pagination?.total || 0;
    messageHandler.confirm(
      'Excluir Mensagens',
      `Tem certeza que deseja excluir ${messageCount} mensagem(ns) filtrada(s)? Esta ação não pode ser desfeita.`,
      () => clearMutation.mutate()
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = (newFilters: MessageScheduleFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const closeModal = () => {
    setModal('');
    setEditingSchedule(null);
  };

  const totalMessages = scheduleResponse?.pagination?.total || 0;
  const totalPages = scheduleResponse?.pagination?.last_page || 1;

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Mensagens Agendadas"
          description="Gerencie suas mensagens agendadas e automações."
          actions={
            <Button
              onClick={() => {
                setEditingSchedule(null);
                setModal('create');
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Mensagem
            </Button>
          }
        />

        <div className="mt-4 space-y-4">
          <MessageFilters
            appliedFilters={filters}
            onSearch={handleSearch}
            onClear={handleClearFilters}
            assistants={assistants}
            sessions={sessions}
            queues={queues}
            templates={templates}
            onExport={handleExport}
            onClearMessages={handleClearMessages}
            totalMessages={totalMessages}
            isExporting={isExporting}
          />

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {totalMessages > 0
                ? `${scheduleResponse?.data?.length || 0} de ${totalMessages} mensagem(ns) exibida(s)`
                : 'Nenhuma mensagem encontrada'}
            </span>
          </div>

          <MessageList
            messages={scheduleResponse?.data || []}
            onEdit={schedule => {
              setEditingSchedule(schedule);
              setModal('edit');
            }}
            onDelete={handleDelete}
            isLoading={isLoading}
            isFiltered={Object.keys(filters).length > 0}
          />

          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>

        <Modal
          isOpen={modal === 'create' || modal === 'edit'}
          onClose={closeModal}
          title={editingSchedule ? 'Editar Mensagem' : 'Nova Mensagem'}
        >
          <MessageForm
            message={editingSchedule}
            assistants={assistants}
            sessions={sessions}
            templates={templates}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default MessageSchedules;
